import { useCallback, useRef, useState } from 'react';
import { cloneThread, countComments, getDemoSocialSeed } from '../../data/promoSocialDemo';

export function usePromoSocial({ isAuthenticated = true, onNeedLogin, currentUser } = {}) {
  const storeRef = useRef({});
  const [storeVersion, setStoreVersion] = useState(0);
  const [likeSheet, setLikeSheet] = useState({ visible: false, item: null, likes: [] });
  const [commentSheet, setCommentSheet] = useState({ visible: false, item: null });
  const [shareSheet, setShareSheet] = useState({ visible: false, item: null });

  const bump = useCallback(() => {
    setStoreVersion((n) => n + 1);
  }, []);

  const ensure = useCallback((itemId) => {
    return storeRef.current[itemId];
  }, []);

  const hydrate = useCallback(
    (item) => {
      if (!item?.id || storeRef.current[item.id]) return;
      storeRef.current[item.id] = {
        liked: !!item.liked_by_me,
        viewCount: Number(item.view_count) || 0,
        likeCount: Number(item.like_count) || 0,
        commentCount: Number(item.comment_count) || 0,
        shareCount: Number(item.share_count) || 0,
        comments: cloneThread(item.comments),
        commentDisplayed: item.comment_displayed,
      };
      bump();
    },
    [bump]
  );

  const getEntry = useCallback(
    (itemId) => {
      const entry = ensure(itemId);
      const commentCount = countComments(entry.comment);

      return {
        liked: entry.liked,
        viewCount: entry.viewCount,
        likeCount: entry.likeCount,
        comments: entry.comments,
        commentCount: commentCount == 0 ? entry.commentCount : commentCount,
        shareCount: entry.shareCount,
        commentDisplayed: entry.commentDisplayed,
      };
    },
    [ensure, storeVersion]
  );

  const guardAuth = useCallback(() => {
    if (isAuthenticated) return true;
    if (onNeedLogin) onNeedLogin();
    return false;
  }, [isAuthenticated, onNeedLogin]);

  const updateCommentDisplayState = useCallback(
    (itemId, displayed) => {
      const entry = ensure(itemId);
      entry.commentDisplayed = displayed;
      bump();
    },
    [bump, ensure]
  );

  const toggleLike = useCallback(
    (itemId) => {
      if (!guardAuth()) return;
      const entry = ensure(itemId);
      entry.liked = !entry.liked;
      entry.likeCount = Math.max(0, entry.likeCount + (entry.liked ? 1 : -1));
      bump();
    },
    [bump, ensure, guardAuth]
  );

  const updateLikeState = useCallback(
    (itemId, liked) => {
      const entry = ensure(itemId);
      entry.liked = liked;
      bump();
    },
    [bump, ensure]
  );

  const updateLikeCount = useCallback(
    (itemId, likeCount) => {
      const entry = ensure(itemId);
      entry.likeCount = likeCount;
      bump();
    },
    [bump, ensure]
  );

  const openLikes = useCallback(
    (item, likes) => {
      ensure(item.id);
      setLikeSheet({ 
        visible: true, 
        item,
        likes
      });
    },
    [ensure]
  );

  const closeLikes = useCallback(() => {
    setLikeSheet({ visible: false, item: null, likes: [] });
  }, []);

  const setComments = useCallback(
    (itemId, comments) => {
      const entry = ensure(itemId);
      entry.comments = comments.sort((a, b) => b.timestamp - a.timestamp);
      bump();
    },
    [bump, ensure]
  );

  const addComment = useCallback(
    (
      itemId, 
      parentId, 
      commentId, 
      userId, 
      userName, 
      userAvatar, 
      text, 
      likers,
      ts, 
      mention = '',
    ) => {
      if (!guardAuth()) return false;
      const trimmed = (text || '').trim();
      if (!trimmed) return false;

      const entry = ensure(itemId);
      const next = {
        id: commentId,
        userId: userId,
        user: userName,
        avatar: userAvatar,
        text: trimmed,
        likers: likers,
        time: 'now',
        timestamp: ts || Date.now(),
        mention: mention || '',
        replies: [],
      };

      const parent = entry.comments.find((c) => c.id === parentId);
      if (parentId) {
        if (parent) {
          parent.replies = updateComments((parent.replies || []), next);
        }
      } else {
        entry.comments = updateComments(entry.comments, next);
        entry.comments = entry.comments.sort((a, b) => b.timestamp - a.timestamp);
      }
      bump();
      return true;
    },
    [bump, currentUser, ensure, guardAuth]
  );

  const deleteComment = useCallback(
    (itemId, commentId) => {
      if (!guardAuth()) return false;
      const entry = ensure(itemId);

      entry.comments.forEach(c => {
        const replyIndex = c.replies.findIndex(r => r.id === commentId);
        if(replyIndex >= 0) {
          c.replies.splice(replyIndex, 1);
        }
      });

      const commentIndex = entry.comments.findIndex(c => c.id === commentId);
      if(commentIndex >= 0) {
        entry.comments.splice(commentIndex, 1);
      }
      bump();
      return true;
    },
    [bump, currentUser, ensure, guardAuth]
  );

  const updateCommentCount = useCallback(
    (itemId, commentCount) => {
      const entry = ensure(itemId);
      entry.commentCount = commentCount;
      bump();
    },
    [bump, ensure]
  );

  const openComments = useCallback(
    (item) => {
      ensure(item.id);
      setCommentSheet({ visible: true, item });
    },
    [ensure]
  );

  const closeComments = useCallback(() => {
    setCommentSheet({ visible: false, item: null });
  }, []);

  const updateCommentLikers = useCallback(
    (itemId, commentId, likers) => {
      const entry = ensure(itemId);
      
      const commentIndex = entry.comments.findIndex(c => c.id === commentId);
      const parentIndex = entry.comments.findIndex(c => !!c.replies.find(r => r.id === commentId));

      if (commentIndex >= 0) {
        if (entry.comments[commentIndex].id === commentId) {
          entry.comments[commentIndex].likers = likers;
        }
      }

      if (parentIndex >= 0) {
        const replyIndex = entry.comments[parentIndex].replies.findIndex(r => r.id === commentId);
        if (replyIndex >= 0) {
          entry.comments[parentIndex].replies[replyIndex].likers = likers;
        }
      }
      bump();
    },
    [bump, currentUser, ensure, guardAuth]
  );

  const updateComments = (comments, comment) => {
    const exists = comments.some(c => c.id === comment.id);
    return exists
      ? comments.map(c => c.id === comment.id ? comment : c)
      : [...comments, comment];
  }

  const updateShareCount = useCallback(
    (itemId, shareCount) => {
      const entry = ensure(itemId);
      entry.shareCount = shareCount;
      bump();
    },
    [bump, ensure]
  );

  const openShare = useCallback((item) => {
    if (guardAuth()) {
      setShareSheet({ visible: true, item });
    }
  }, []);

  const closeShare = useCallback(() => {
    setShareSheet({ visible: false, item: null });
  }, []);

  const updateViewCount = useCallback(
    (itemId, viewCount) => {
      const entry = ensure(itemId);
      entry.viewCount = viewCount;
      bump();
    },
    [bump, ensure]
  );

  return {
    getEntry,
    hydrate,
    updateCommentDisplayState,
    toggleLike,
    updateLikeState,
    updateLikeCount,
    openLikes,
    closeLikes,
    setComments,
    addComment,
    deleteComment,
    updateCommentCount,
    openComments,
    closeComments,
    updateCommentLikers,
    updateShareCount,
    openShare,
    closeShare,
    updateViewCount,
    likeSheet,
    commentSheet,
    shareSheet,
    currentUser,
    guardAuth,
  };
}
